import { z } from 'zod';
/**
 * Placing a client with insurers (task 93, decision 0070). For each insurer form chosen for an assessment,
 * the broker records where it stands with that insurer (preparing, submitted, quoted, bound, declined or not
 * taken up), the quotes the insurer gave, and which one was bound. The quotes of all the client's insurers
 * are compared side by side by limit.
 */
export const PLACEMENT_STATUSES = [
    'preparing',
    'submitted',
    'quoted',
    'bound',
    'declined',
    'not_taken',
];
export const PLACEMENT_LABEL = {
    preparing: 'Preparing',
    submitted: 'Submitted',
    quoted: 'Quoted',
    bound: 'Bound',
    declined: 'Declined',
    not_taken: 'Not taken up',
};
export const PLACEMENT_TONE = {
    preparing: 'neutral',
    submitted: 'warn',
    quoted: 'warn',
    bound: 'ok',
    declined: 'bad',
    not_taken: 'neutral',
};
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const Money = z.number().min(0).max(100_000_000_000);
/** One option an insurer quoted: a limit, its deductible and the premium (RM, before taxes). */
export const PlacementQuote = z.strictObject({
    limit: Money.positive(),
    deductible: Money,
    premium: Money,
    note: z.string().trim().max(500).optional(),
});
export const PlacementUpdate = z
    .strictObject({
    status: z.enum(PLACEMENT_STATUSES),
    quotes: z.array(PlacementQuote).max(10).default([]),
    /** The quote bound (its position in `quotes`), the policy number and when cover starts. */
    bound: z
        .strictObject({
        quote: z.number().int().min(0),
        policy_number: z.string().trim().min(1).max(60),
        inception: Day,
    }) // prettier-ignore
        .optional(),
    note: z.string().trim().max(2000).optional(),
    /** The placement's revision as last read; 0 for a new one. A newer revision is a 409. */
    revision: z.number().int().min(0),
})
    .superRefine((p, ctx) => {
    if ((p.status === 'quoted' || p.status === 'bound') && !p.quotes.length)
        ctx.addIssue({ code: 'custom', message: 'Add the quote the insurer gave', path: ['quotes'] });
    if (p.status === 'bound' && !p.bound)
        ctx.addIssue({ code: 'custom', message: 'Say which quote was bound, the policy number and inception', path: ['bound'] }); // prettier-ignore
    if (p.status !== 'bound' && p.bound)
        ctx.addIssue({ code: 'custom', message: 'Only a bound placement has a bound quote', path: ['bound'] });
    if (p.bound && p.bound.quote >= p.quotes.length)
        ctx.addIssue({ code: 'custom', message: 'The bound quote must be one of the quotes', path: ['bound', 'quote'] }); // prettier-ignore
});
/**
 * The quotes side by side: a row per insurer, a column per limit. Declined and not-taken-up placements keep
 * their row (the broker sees who said no) but have no quotes. Where an insurer quoted one limit twice, the
 * cheaper option is shown.
 */
export function compareQuotes(placements) {
    const limits = [...new Set(placements.flatMap((p) => p.quotes.map((q) => q.limit)))].sort((a, b) => a - b);
    const rows = placements.map((p) => {
        const cells = {};
        p.quotes.forEach((q, i) => {
            const was = cells[q.limit];
            if (!was || q.premium < was.premium)
                cells[q.limit] = {
                    premium: q.premium,
                    deductible: q.deductible,
                    lowest: false,
                    bound: p.bound?.quote === i,
                };
        });
        return { form_key: p.form_key, insurer: p.insurer, status: p.status, cells };
    });
    for (const limit of limits) {
        const live = rows.filter((r) => r.cells[limit] && r.status !== 'declined' && r.status !== 'not_taken');
        const min = Math.min(...live.map((r) => r.cells[limit].premium));
        if (live.length > 1)
            for (const r of live)
                if (r.cells[limit].premium === min)
                    r.cells[limit].lowest = true;
    }
    return { limits, rows };
}
/** When cover starting on `inception` renews: the day before its first anniversary. */
export function renewalDate(inception) {
    const d = new Date(`${inception}T00:00:00Z`);
    d.setUTCFullYear(d.getUTCFullYear() + 1);
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
}
//# sourceMappingURL=placements.js.map